import React, { useState, useEffect, useRef } from 'react';
import Select from 'react-select';
import './RazorPayauth.css';
import { useUser } from '../context/UserContext';
import Swal from 'sweetalert2';
import { buildDiffPreview, fireAudit } from '../utils/auditClient';

const RazorPayauth = () => {
  const [orderType, setOrderType] = useState(null);
  const [dueOrderId, setDueOrderId] = useState('');
  const [amount, setAmount] = useState('');
  const [paymentId, setPaymentId] = useState('');
  const [orderTypeOptions, setOrderTypeOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const { userData } = useUser();
  const token = localStorage.getItem('auth-token');
  const userId = userData?.userId;
  const baselineRef = useRef(null);

  const buildSubmitPayload = (local = { orderType, dueOrderId, amount, paymentId }) => ({
    dueOrderId: local.dueOrderId ? Number(local.dueOrderId) : null,
    amount: local.amount ? Number(local.amount) : null,
    amountPaid: local.amount ? Number(local.amount) : null,
    razorpayPaymentId: local.paymentId || '',
    orderType: local.orderType?.value || '',
    participantUserId: userId || null,
  });

  useEffect(() => {
    baselineRef.current = buildSubmitPayload({
      orderType: null,
      dueOrderId: '',
      amount: '',
      paymentId: '',
    });
  }, [userId]);

  useEffect(() => {
    const fetchOrderTypes = async () => {
      try {
        if (!userId) return;

        const res = await fetch(`${process.env.REACT_APP_API_RAZORPAY_AUTH}?userId=${userId}`, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        });

        const result = await res.json();

        if (result.success && result.data?.orderType) {
          const formatted = result.data.orderType.map((type) => ({
            value: type,
            label: type.replace(/_/g, ' '),
          }));
          setOrderTypeOptions(formatted);
        } else if (result.data?.Error === 'Unauthorized user') {
          Swal.fire({
            title: 'Error!',
            text: 'Unauthorized user. Please check your credentials.',
            icon: 'error',
            toast: true,
            position: 'top-end',
            showConfirmButton: false,
            timer: 3000,
          });
        }
      } catch {
      console.log('error while fetching')
      }
    };

    fetchOrderTypes();
  }, [userId, token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!orderType?.value) {
      Swal.fire({
        title: 'Missing Order Type',
        text: 'Please select an order type.',
        icon: 'warning',
        toast: true,
        position: 'top-end',
        showConfirmButton: false,
        timer: 2500,
      });
      return;
    }
    if (!dueOrderId || Number.isNaN(Number(dueOrderId))) {
      Swal.fire({
        title: 'Invalid Due Order ID',
        text: 'Please enter a valid numeric order ID.',
        icon: 'warning',
        toast: true,
        position: 'top-end',
        showConfirmButton: false,
        timer: 2500,
      });
      return;
    }
    if (!amount || Number(amount) <= 0) {
      Swal.fire({
        title: 'Invalid Amount',
        text: 'Amount must be greater than 0.',
        icon: 'warning',
        toast: true,
        position: 'top-end',
        showConfirmButton: false,
        timer: 2500,
      });
      return;
    }
    if (!paymentId?.trim()) {
      Swal.fire({
        title: 'Missing Payment ID',
        text: 'Please enter the Razorpay Payment ID.',
        icon: 'warning',
        toast: true,
        position: 'top-end',
        showConfirmButton: false,
        timer: 2500,
      });
      return;
    }

    const newPayload = buildSubmitPayload({ orderType, dueOrderId, amount, paymentId });
    const oldPayload = baselineRef.current || {};
    const { digest, html } = buildDiffPreview(oldPayload, newPayload);
    const confirm = await Swal.fire({
      title: 'Authorize this payment?',
      html: digest.length ? html : '<div style="text-align:left;font-size:13px">Submitting initial authorization.</div>',
      showCancelButton: true,
      confirmButtonText: 'Authorize',
    });
    if (!confirm.isConfirmed) return;

    try {
      setLoading(true);
      const response = await fetch(`${process.env.REACT_APP_API_RAZORPAY_AUTH}?userId=${userId}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(newPayload),
      });

      const result = await response.json();

      if (result.success) {
        fireAudit({
          entity: 'razorpay_auth',
          affected_id: result?.data?.id || null,
          action: 'create',
          businessApi: `${process.env.REACT_APP_API_RAZORPAY_AUTH}?userId=${userId}`,
          businessMethod: 'POST',
          oldPayload,
          newPayload,
          section: 'Razorpay Auth',
          subSection: 'Razorpay Auth Form',
          extra: { page: 'RazorPayauth' },
        });

        baselineRef.current = newPayload;

        Swal.fire({
          title: 'Success!',
          text: 'Payment Authorized Successfully!',
          icon: 'success',
          toast: true,
          position: 'top-end',
          showConfirmButton: false,
          timer: 3000,
        });
      } else {
        Swal.fire({
          title: 'Error!',
          text: result?.success_message || 'Payment Authorization Failed!',
          icon: 'error',
          toast: true,
          position: 'top-end',
          showConfirmButton: false,
          timer: 3000,
        });
      }
    } catch (error) {
      Swal.fire({
        title: 'Error!',
        text: error?.message || 'An error occurred while processing your request',
        icon: 'error',
        toast: true,
        position: 'top-end',
        showConfirmButton: false,
        timer: 3000,
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="enrollment-container">
      <form onSubmit={handleSubmit} className="walkin-enquiry-form">
        <div className="floating-select-form" style={{ width: '98%' }}>
          <Select
            id="orderType"
            name="orderType"
            options={orderTypeOptions}
            value={orderType}
            onChange={setOrderType}
            placeholder="Select Order Type"
            isClearable
            className={`walkin-form-dropdown ${orderType ? 'has-value' : ''}`}
            classNamePrefix="walkin-select"
            styles={{
              control: (provided) => ({ ...provided, padding: '5px' }),
              placeholder: (provided) => ({ ...provided, color: 'black' }),
            }}
          />
          {orderType && <label htmlFor="orderType">Order Type</label>}
        </div>

        <div className="floating-select-form" style={{ width: '98%' }}>
          <input
            type="text"
            id="dueOrderId"
            name="dueOrderId"
            className={`walkin-form ${dueOrderId ? 'has-value' : ''}`}
            value={dueOrderId}
            onChange={(e) => {
              const val = e.target.value.replace(/\D/g, '');
              setDueOrderId(val);
            }}
            placeholder="Enter Due Order ID"
            required
          />
          {dueOrderId && <label htmlFor="dueOrderId">Due Order ID</label>}
        </div>

        <div className="floating-select-form" style={{ width: '98%' }}>
          <input
            type="number"
            id="amount"
            name="amount"
            className={`walkin-form ${amount ? 'has-value' : ''}`}
            value={amount}
            min="0"
            step="0.01"
            onChange={(e) => setAmount(e.target.value)}
            onWheel={(e) => e.target.blur()}
            onKeyDown={(e) => ['ArrowUp', 'ArrowDown'].includes(e.key) && e.preventDefault()}
            placeholder="Enter Amount"
            required
          />
          {amount && <label htmlFor="amount">Amount</label>}
        </div>

        <div className="floating-select-form" style={{ width: '98%' }}>
          <input
            type="text"
            id="paymentId"
            name="paymentId"
            className={`walkin-form ${paymentId ? 'has-value' : ''}`}
            value={paymentId}
            onChange={(e) => setPaymentId(e.target.value)}
            placeholder="Enter Razorpay Payment ID"
            required
          />
          {paymentId && <label htmlFor="paymentId">Razorpay Payment ID</label>}
        </div>

        <button type="submit" className="submit-button" disabled={loading}>
          {loading ? 'Authorizing…' : 'Submit'}
        </button>
      </form>
    </div>
  );
};

export default RazorPayauth;
